import { useEffect, useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Calendar,
  Filter,
  DollarSign,
  User,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { getAuthHeader } from "../context/AuthContext";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function OrdersPageImproved() {
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [tables, setTables] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    fetchOrders(page);
  }, [page]);

  useEffect(() => {
    applyFilters();
  }, [orders, searchTerm, statusFilter, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, typeFilter, dateFrom, dateTo]);

  const fetchOrders = async (pageNo = 1) => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: getAuthHeader(),
        params: {
          page: pageNo,
          limit: limit,
        },
      });
      setOrders(response.data.data);
      setTotalPages(response.data.total_pages);
      setTotalOrders(response.data.total_orders);
      setPage(response.data.page);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    }
  };

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeader(),
      });
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  };

  const applyFilters = () => {
    let filtered = [...orders];

    // Search filter (order ID, customer name, phone)
    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.customer_phone?.includes(searchTerm)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (order) => order.order_status === statusFilter
      );
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((order) => order.order_type === typeFilter);
    }

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.created_at)
          .toISOString()
          .split("T")[0];
        return orderDate >= dateFrom;
      });
    }

    if (dateTo) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.created_at)
          .toISOString()
          .split("T")[0];
        return orderDate <= dateTo;
      });
    }

    setFilteredOrders(filtered);
  };

  const getTableName = (tableId) => {
    const table = tables.find((t) => t.id === tableId);
    return table ? table.table_name : "N/A";
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API}/orders/${orderId}/status`,
        { order_status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success("Order status updated");
      fetchOrders();
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
      preparing: "bg-blue-100 text-blue-700 border-blue-300",
      ready: "bg-purple-100 text-purple-700 border-purple-300",
      served: "bg-green-100 text-green-700 border-green-300",
      completed: "bg-gray-100 text-gray-700 border-gray-300",
      cancelled: "bg-red-100 text-red-700 border-red-300",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const statusFlow = ["pending", "preparing", "ready", "served", "completed"];

  const getOrderTypeIcon = (type) => {
    if (type === "dine_in") return "🪑";
    if (type === "takeaway") return "📦";
    if (type === "delivery") return "🛵";
    return "📝";
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Orders Management
          </h1>
          <p className="text-gray-600">View and manage all customer orders</p>
        </div>

        {/* Filters Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter size={20} />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div>
                <Label>Search</Label>
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <Input
                    placeholder="Order ID, Name, Phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="preparing">Preparing</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="served">Served</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Type Filter */}
              <div>
                <Label>Order Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="dine_in">Dine-In</SelectItem>
                    <SelectItem value="takeaway">Takeaway</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date From */}
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Date To */}
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing {filteredOrders.length} of {orders.length} orders
              </div>
              <Button variant="outline" onClick={clearFilters} size="sm">
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg">No orders found</p>
                <p className="text-gray-400 text-sm mt-2">
                  Try adjusting your filters
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredOrders.map((order) => {
              const currentIndex = statusFlow.indexOf(order.order_status);
              const nextStatus = statusFlow[currentIndex + 1];

              return (
                <Card key={order.id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      {/* Order Info */}
                      <div className="lg:col-span-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-2xl">
                                {getOrderTypeIcon(order.order_type)}
                              </span>
                              <h3 className="font-bold text-lg">
                                Order #{order.id.slice(0, 8)}
                              </h3>
                            </div>
                            <p className="text-sm text-gray-600">
                              {new Date(order.created_at).toLocaleDateString()}{" "}
                              at{" "}
                              {new Date(order.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                              order.order_status
                            )}`}
                          >
                            {order.order_status.toUpperCase()}
                          </span>
                        </div>

                        {order.table_id && (
                          <div className="flex items-center gap-2 text-sm mb-2">
                            <span className="text-gray-600">Table:</span>
                            <span className="font-semibold">
                              {getTableName(order.table_id)}
                            </span>
                          </div>
                        )}

                        {order.customer_name && (
                          <div className="flex items-center gap-2 text-sm mb-2">
                            <User size={14} className="text-gray-400" />
                            <span>{order.customer_name}</span>
                            {order.customer_phone && (
                              <span className="text-gray-600">
                                • {order.customer_phone}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-sm mt-3">
                          {/* <DollarSign size={16} className="text-green-600" /> */}
                          <span className="font-bold text-lg text-green-600">
                            ${order.grand_total.toFixed(2)}
                          </span>
                          <span className="text-gray-600 text-xs">
                            ({order.items?.length || 0} items)
                          </span>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="lg:col-span-5">
                        <h4 className="font-semibold mb-2 text-sm text-gray-700">
                          Order Items:
                        </h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {order.items?.map((item, idx) => (
                            <div
                              key={idx}
                              className="text-sm flex justify-between bg-gray-50 p-2 rounded"
                            >
                              <span>
                                {item.quantity}x {item.item_name}
                                {item.modifiers &&
                                  item.modifiers.length > 0 && (
                                    <span className="text-xs text-gray-500 ml-1">
                                      (
                                      {item.modifiers
                                        .map((m) => m.option_name)
                                        .join(", ")}
                                      )
                                    </span>
                                  )}
                              </span>
                              <span className="font-semibold">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="lg:col-span-3 flex flex-col justify-center gap-2">
                        {nextStatus &&
                          order.order_status !== "completed" &&
                          order.order_status !== "cancelled" && (
                            <Button
                              onClick={() =>
                                updateOrderStatus(order.id, nextStatus)
                              }
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              size="sm"
                            >
                              Mark as{" "}
                              {nextStatus.charAt(0).toUpperCase() +
                                nextStatus.slice(1)}
                            </Button>
                          )}

                        {order.order_status === "pending" && (
                          <Button
                            onClick={() =>
                              updateOrderStatus(order.id, "cancelled")
                            }
                            variant="outline"
                            className="w-full border-red-300 text-red-600 hover:bg-red-50"
                            size="sm"
                          >
                            Cancel Order
                          </Button>
                        )}

                        {(order.order_status === "completed" ||
                          order.order_status === "cancelled") && (
                          <div className="text-center text-sm text-gray-500">
                            Order {order.order_status}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8 bg-white p-4 rounded-lg shadow border">
            <span className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b> ({totalOrders} orders)
            </span>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>

              {[...Array(totalPages)].map((_, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={page === i + 1 ? "default" : "outline"}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}

              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
